import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#1a1d23",
  },
  spinner: {
    display: "inline-block",
    width: 20,
    height: 20,
    border: "2px solid #e5e7eb",
    borderTopColor: "#6b7280",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 8,
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    marginBottom: "0.875rem",
  },
  cardBody: { padding: "1rem 1.125rem" },
  sectionTitle: (color) => ({
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color,
    marginBottom: "0.75rem",
  }),
  pill: (variant) => {
    const map = {
      green: { background: "#F0FDF4", color: "#166534" },
      blue: { background: "#EFF6FF", color: "#1E40AF" },
      gray: { background: "#F9FAFB", color: "#4B5563", border: "1px solid #e5e7eb" },
    };
    return {
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 9px",
      borderRadius: 20,
      whiteSpace: "nowrap",
      display: "inline-block",
      ...(map[variant] || map.gray),
    };
  },
  btn: {
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 12px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  fieldLabel: { fontSize: 12, color: "#6b7280" },
  fieldValue: { fontSize: 13, color: "#1a1d23", fontWeight: 500 },
  divider: { height: 1, background: "#f3f4f6", margin: "0.75rem 0" },
  errorCard: {
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 10,
    padding: "1rem 1.125rem",
    color: "#991B1B",
    fontSize: 13,
  },
  emptyCard: {
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "3rem",
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 13,
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatCurrency = (n) =>
  new Intl.NumberFormat("en-TT", {
    style: "currency",
    currency: "TTD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n ?? 0);

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        timeZone: "America/Port_of_Spain",
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

// ── Sub-components ────────────────────────────────────────────────────────────
const Spinner = () => (
  <>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <span style={s.spinner} />
  </>
);

const Pill = ({ variant, children }) => <span style={s.pill(variant)}>{children}</span>;

const Field = ({ label, value, highlight }) => (
  <div>
    <div style={s.fieldLabel}>{label}</div>
    <div style={{ ...s.fieldValue, color: highlight || "#1a1d23" }}>{value}</div>
  </div>
);

// ── Lease card ────────────────────────────────────────────────────────────────
const LeaseCard = ({ tenant, lease, status }) => {
  const navigate = useNavigate();

  const pillConfig = {
    current: { variant: "green", label: "Current" },
    linked: { variant: "blue", label: "Linked" },
    past: { variant: "gray", label: "Past" },
  };
  const pill = pillConfig[status] || pillConfig.past;

  const accentColor =
    {
      current: "#166534",
      linked: "#1E40AF",
      past: "#d1d5db",
    }[status] || "#d1d5db";

  return (
    <div style={{ ...s.card, borderLeft: `3px solid ${accentColor}` }}>
      <div style={s.cardBody}>
        {/* Header row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: "0.75rem",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 3,
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                {tenant.name || "Unnamed tenant"}
              </span>
              <Pill variant={pill.variant}>{pill.label}</Pill>
            </div>
            <div
              style={{ fontSize: 12, color: "#6b7280", display: "flex", gap: 12, flexWrap: "wrap" }}
            >
              {tenant.email && <span>{tenant.email}</span>}
              {tenant.phone && <span>{tenant.phone}</span>}
            </div>
          </div>
          <div style={{ flexShrink: 0 }}>
            <button style={s.btn} onClick={() => navigate(`/tenants/${tenant._id}`)}>
              View tenant →
            </button>
          </div>
        </div>

        <div style={s.divider} />

        {/* Lease fields grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "0.75rem",
          }}
        >
          <Field label="Lease start" value={formatDate(lease.leaseStart)} />
          <Field label="Lease end" value={formatDate(lease.leaseEnd)} />
          {lease.actualLeaseEnd && (
            <Field label="Actual end" value={formatDate(lease.actualLeaseEnd)} />
          )}
          {lease.terminationReason && (
            <Field label="Termination reason" value={lease.terminationReason} />
          )}
          <Field
            label="Monthly rent"
            value={formatCurrency(lease.monthlyRent)}
            highlight="#166534"
          />
          <Field label="Security deposit" value={formatCurrency(lease.securityDeposit)} />
        </div>
      </div>
    </div>
  );
};

// ── Section wrapper ───────────────────────────────────────────────────────────
const Section = ({ title, color, items, status }) => {
  if (!items.length) return null;
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <div style={s.sectionTitle(color)}>{title}</div>
      {items.map(({ tenant, lease }) => (
        <LeaseCard key={lease._id} tenant={tenant} lease={lease} status={status} />
      ))}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const PropertyTenantsAndLeases = ({ property, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    if (!property?._id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${API_ENDPOINTS.tenants.getLeaseHistory}?propertyId=${property._id}`,
          {
            headers: {
              "Content-Type": "application/json",
              "ngrok-skip-browser-warning": "true",
              ...getAuthHeaders(),
            },
          }
        );
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(data?.message || "Failed to load tenants and leases");
        setItems(data.data || []);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?._id, refreshKey]);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "3rem 0", color: "#6b7280", fontSize: 14 }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <Spinner /> Loading tenants and leases…
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div style={s.errorCard}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Unable to load tenants and leases</div>
        <div>{error}</div>
      </div>
    );
  }

  // ── Derived lists ─────────────────────────────────────────────────────────
  const isOneTimeRental = property?.type === "one_time_rental";
  const now = new Date();

  const current = isOneTimeRental
    ? []
    : items.filter(({ lease }) => {
        const end = lease.actualLeaseEnd
          ? new Date(lease.actualLeaseEnd)
          : new Date(lease.leaseEnd);
        return new Date(lease.leaseStart) <= now && end >= now;
      });

  const linked = isOneTimeRental ? items : [];

  const past = isOneTimeRental
    ? []
    : items.filter(({ lease }) => {
        const end = lease.actualLeaseEnd
          ? new Date(lease.actualLeaseEnd)
          : new Date(lease.leaseEnd);
        return end < now;
      });

  // ── Empty state ───────────────────────────────────────────────────────────
  if (items.length === 0) {
    return (
      <div style={s.page}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={s.emptyCard}>No tenants or lease history for this property yet.</div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      <Section title="Current tenants" color="#166534" items={current} status="current" />
      <Section title="Linked tenants" color="#1E40AF" items={linked} status="linked" />
      <Section title="Past tenants & leases" color="#6b7280" items={past} status="past" />
    </div>
  );
};

export default PropertyTenantsAndLeases;
